import type { ReactNode } from "react";
import { Skeleton } from "./skeleton";

export interface Column<Row> {
  header: string;
  cell: (row: Row) => ReactNode;
  className?: string;
}

interface DataTableProps<Row> {
  columns: Column<Row>[];
  rows: Row[] | undefined;
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
}

export function DataTable<Row>({
  columns,
  rows,
  isLoading,
  isError,
  emptyMessage = "Nothing here.",
  rowKey,
  onRowClick,
}: DataTableProps<Row>) {
  if (isLoading) {
    return (
      <div className="p-5 space-y-3">
        {Array.from({ length: 6 }).map((_, r) => (
          <div key={r} className="flex items-center gap-4">
            {columns.map((_, c) => (
              <Skeleton
                key={c}
                className="h-3 flex-1"
                style={{ maxWidth: `${100 - c * 8}%` }}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="px-5 py-12 text-center text-sm text-[var(--color-error)]">
        Couldn't load data.
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="px-5 py-12 text-center text-sm text-[var(--color-text-muted)]">
        {emptyMessage}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-[var(--color-text-subtle)] border-b border-[var(--color-border)]">
            {columns.map((col, i) => (
              <th
                key={i}
                className={["px-5 py-3 font-medium", col.className].filter(Boolean).join(" ")}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={() => onRowClick?.(row)}
              className={[
                "border-b border-[var(--color-border)] last:border-b-0",
                onRowClick
                  ? "cursor-pointer hover:bg-[var(--color-elevated)]"
                  : "",
              ].join(" ")}
            >
              {columns.map((col, i) => (
                <td
                  key={i}
                  className={["px-5 py-3 text-[var(--color-text)]", col.className]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {col.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
